import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import {
    Globe,
    Search,
    Loader2,
    Settings2,
    BookOpen,
    ArrowRight
} from 'lucide-react';
import { compareAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

export default function ComparePage() {
    const { t } = useTranslation();
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleAnalyze = async () => {
        if (!question.trim()) {
            toast.error('Please enter a question to compare.');
            return;
        }

        setLoading(true);
        try {
            const response = await compareAPI.compare({
                question: question.trim()
            });
            setResult(response.data);
        } catch (error) {
            console.error('Analysis error:', error);
            toast.error('Failed to perform comparative analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="compare-page">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
                    <Globe className="h-4 w-4" />
                    <span>{t('compare.title')}</span>
                </div>
                <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {t('compare.title')}
                </h1>
                <p className="text-muted-foreground max-w-2xl">
                    {t('compare.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Input Form */}
                <div className="lg:col-span-1 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center">
                                <Settings2 className="h-5 w-5 mr-2" />
                                Comparison Parameters
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Research Question</label>
                                <Textarea
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder={t('compare.inputPlaceholder')}
                                    className="min-h-[120px] resize-none"
                                    data-testid="compare-input"
                                />
                            </div>

                            <Button
                                onClick={handleAnalyze}
                                disabled={loading || !question.trim()}
                                className="w-full"
                                data-testid="compare-analyze-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('compare.analyzing')}
                                    </>
                                ) : (
                                    <>
                                        <Search className="mr-2 h-4 w-4" />
                                        {t('compare.analyzeButton')}
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column - Results */}
                <div className="lg:col-span-2 space-y-6">
                    {result ? (
                        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">

                            {/* Comparative Synthesis Conclusion */}
                            <Card className="border-accent/50 bg-accent/5">
                                <CardHeader>
                                    <CardTitle className="text-xl flex items-center text-accent">
                                        <Globe className="h-5 w-5 mr-2" />
                                        {t('compare.synthesisTitle')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="font-semibold leading-relaxed">
                                        {result.comparison}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Canon Stance */}
                            <Card>
                                <CardHeader className="pb-3 border-b border-border/50">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-lg flex items-center">
                                            <BookOpen className="h-5 w-5 mr-2" />
                                            {t('compare.canonTitle')}
                                        </CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    <p className="leading-relaxed mb-4">{result.canon_analysis}</p>

                                    {result.citations?.canon?.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-border/50">
                                            <h4 className="text-sm font-medium text-muted-foreground mb-3">{t('results.applicableArticles')}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {result.citations.canon.map((artId) => (
                                                    <Link key={artId} to={`/article/${artId}`}>
                                                        <Badge variant="secondary" className="hover:bg-accent hover:text-white transition-colors cursor-pointer">
                                                            Article {artId} <ArrowRight className="h-3 w-3 ml-1" />
                                                        </Badge>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Global Constitutional Alignments */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* USA */}
                                <Card>
                                    <CardHeader className="pb-3 bg-muted/30">
                                        <CardTitle className="text-sm font-semibold">{t('compare.usaTitle')}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-4 text-sm">
                                        <p className="mb-3">{result.constitution_analysis.usa}</p>
                                        {result.citations?.usa?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-auto">
                                                {result.citations.usa.map((ref, i) => (
                                                    <Badge key={i} variant="outline" className="text-[10px]">{ref}</Badge>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Greece */}
                                <Card>
                                    <CardHeader className="pb-3 bg-muted/30">
                                        <CardTitle className="text-sm font-semibold">{t('compare.greeceTitle')}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-4 text-sm">
                                        <p className="mb-3">{result.constitution_analysis.greece}</p>
                                        {result.citations?.greece?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-auto">
                                                {result.citations.greece.map((ref, i) => (
                                                    <Badge key={i} variant="outline" className="text-[10px]">{ref}</Badge>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* EU Charter */}
                                <Card>
                                    <CardHeader className="pb-3 bg-muted/30">
                                        <CardTitle className="text-sm font-semibold">{t('compare.euTitle')}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-4 text-sm">
                                        <p className="mb-3">{result.constitution_analysis.eu_charter}</p>
                                    </CardContent>
                                </Card>
                            </div>

                        </div>
                    ) : (
                        <Card className="h-full flex flex-col items-center justify-center p-8 text-center text-muted-foreground min-h-[400px]">
                            <Globe className="h-16 w-16 mb-4 opacity-20" />
                            <h3 className="text-lg font-medium mb-2">{t('results.noAnalysis')}</h3>
                            <p className="max-w-md">{t('results.noAnalysisDesc')}</p>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
